import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface FavoritesContextValue {
  favoriteIds: Set<string>;
  isFavorite: (modelId: string) => boolean;
  addFavorite: (modelId: string, modelName: string) => Promise<void>;
  removeFavorite: (modelId: string) => Promise<void>;
  loading: boolean;
}

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setFavoriteIds(new Set());
      return;
    }
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from("user_favorite_models")
        .select("model_id")
        .eq("user_id", user.id);
      if (cancelled) return;
      setFavoriteIds(new Set((data ?? []).map((r: any) => r.model_id)));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const isFavorite = useCallback((id: string) => favoriteIds.has(id), [favoriteIds]);

  const addFavorite = useCallback(
    async (modelId: string, modelName: string) => {
      if (!user) return;
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        next.add(modelId);
        return next;
      });
      const { error } = await supabase
        .from("user_favorite_models")
        .insert({ user_id: user.id, model_id: modelId, model_name: modelName, is_demo: false });
      if (error) {
        setFavoriteIds((prev) => {
          const next = new Set(prev);
          next.delete(modelId);
          return next;
        });
        throw error;
      }
    },
    [user]
  );

  const removeFavorite = useCallback(
    async (modelId: string) => {
      if (!user) return;
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        next.delete(modelId);
        return next;
      });
      const { error } = await supabase
        .from("user_favorite_models")
        .delete()
        .eq("user_id", user.id)
        .eq("model_id", modelId);
      if (error) {
        setFavoriteIds((prev) => {
          const next = new Set(prev);
          next.add(modelId);
          return next;
        });
        throw error;
      }
    },
    [user]
  );

  const value = useMemo(
    () => ({ favoriteIds, isFavorite, addFavorite, removeFavorite, loading }),
    [favoriteIds, isFavorite, addFavorite, removeFavorite, loading]
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used within FavoritesProvider");
  return ctx;
}
